import { Component, OnInit, TemplateRef, ViewChild, viewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatAccordion } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { Alert, AlertService, AlertType, ModalComponent, ModalResult, TableConfig } from '@michelin/theme';
import { OrderDto } from 'src/app/shared/models/order/order-dto';
import { OrderItemDto } from 'src/app/shared/models/order/order-item-dto';
import { OrderService } from 'src/app/shared/services/order/order.service';
import { formatIsoDate } from 'src/app/shared/utils/functions';
import { InvoiceDto, InvoiceNoIdDto } from 'src/app/shared/models/order/invoice-dto';
import { forkJoin } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InvoiceItemDto } from 'src/app/shared/models/order/invoice-item-dto';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { UserLoginResponseDto } from 'src/app/shared/models/auth/auth-dto';

@Component({
    selector: 'app-show-order',
    templateUrl: './show-order.component.html',
    styleUrl: './show-order.component.scss',
    standalone: false
})
export class ShowOrderComponent implements OnInit {
  @ViewChild('modalConfirmCancelItem') modalConfirmCancelItem: ModalComponent;
  @ViewChild('contentModalConfirmCancelItem') contentModalConfirmCancelItem: TemplateRef<HTMLElement>;
  selectedItem: OrderDto;
  loading = false;
  orderId: number;
  userData: UserLoginResponseDto = null;

  data = {
    order: {} as OrderDto,
    invoices: [] as InvoiceDto[]
  };

  selectedFile: File;
  selectedFiles: FileList;
  invoiceForm: FormGroup = null;

  formatIsoDate = formatIsoDate;

  formatCurrency = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

  tableConfig: TableConfig<OrderItemDto> = {
    columns: {
      partNumber: { header: 'Part #', cell: (item) => item.partNumber },
      cai: { header: 'CAI', cell: (item) => item.product?.cai?.toString() },
      description: { header: 'Descrição', cell: (item) => item.product?.description?.toString() },
      stockDes: { header: 'Estoque Michelin (GAP de até 2 dias)', cell: (item) => item.stockQuantity?.toString() },
      quantity: { header: 'QTD', cell: (item) => item.quantity?.toString() },
      price: { header: 'Sugerido', cell: (item) => this.formatCurrency.format(item.sellout || 0) },
      wrongPartNumber: { header: '   ', cell: 'partNumber' }
    }
  };

  invoiceTableConfig: TableConfig<InvoiceItemDto> = {
    columns: {
      partNumber: { header: 'Part #', width: '100px', cell: (item) => item.items.partNumber },
      cai: { header: 'CAI', width: '100px', cell: (item) => item.items.product?.cai },
      description: { header: 'Descrição', width: '300px', cell: (item) => item.items.product?.description },
      quantityServed: { header: 'Qtd. Atendido', width: '80px', cell: (item) => item.quantity.toString() },
      unitPrice: {
        header: 'R$ Un.',
        width: '100px',
        align: 'right',
        cell: (item) => this.formatCurrency.format(item.itemValue)
      },
      totalPrice: {
        header: 'R$ Total',
        width: '100px',
        cell: (item) => this.formatCurrency.format(item.totalPrice),
        align: 'right'
      }
    },
    groupsColumns: [
      {
        name: '1',
        header: 'Itens',
        columns: ['partNumber', 'cai', 'description']
      },
      {
        name: '2',
        header: 'Quantidade',
        columns: ['quantityServed']
      },
      {
        name: '3',
        header: 'Sellout',
        columns: ['unitPrice', 'totalPrice']
      }
    ]
  };

  orderItemsWithPriceTableConfig: TableConfig<OrderItemDto> = {
    columns: {
      partNumber: { header: 'Part #', width: '100px', cell: (item) => item.partNumber },
      cai: { header: 'CAI', width: '100px', cell: (item) => item.product?.cai },
      description: { header: 'Descrição', width: '300px', cell: (item) => item.product?.description },
      cancelItem: { header: ' ', cell: 'cancelItem', width: '10px', sortable: false },
      quantity: { header: 'Qtd. Pedido', width: '80px', cell: (item) => item.quantity?.toString() },
      quantityServedOrder: { header: 'Qtd. Atendido', width: '80px', cell: (item) => item.quantityServed?.toString() },
      quantityInvoice: { header: 'Qtd.', width: '150px', cell: 'quantityInvoice' },
      stockDes: { header: 'Estoque Michelin\n(GAP de até 2 dias)', width: '100px', cell: (item) => item.stockQuantity?.toString() },
      unitPrice: { header: 'R$ Un.', width: '100px', align: 'right', cell: (item) => this.formatCurrency.format(item.sellout || 0) },
      totalPrice: { header: 'R$ Total', width: '100px', cell: 'totalPrice', align: 'right' }
    },
    groupsColumns: [
      {
        name: '1',
        header: 'Itens',
        columns: ['partNumber', 'cai', 'description']
      },
      {
        name: '2',
        header: 'Quantidade',
        align: 'center',
        columns: ['cancelItem', 'quantity', 'quantityServedOrder', 'quantityInvoice', 'stockDes']
      },
      {
        name: '3',
        header: 'Sellout',
        align: 'center',
        columns: ['unitPrice', 'totalPrice']
      }
    ]
  };

  orderItemsDataSource: MatTableDataSource<OrderItemDto>;
  orderItemsWithPriceDataSource: MatTableDataSource<OrderItemDto>;

  accordion = viewChild.required(MatAccordion);

  constructor(
    private readonly orderService: OrderService,
    private readonly alertService: AlertService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService
  ) {
    this.invoiceForm = formBuilder.group({
      invoiceNumber: this.formBuilder.control('', Validators.required),
      invoiceDate: this.formBuilder.control('', Validators.required),
      file: this.formBuilder.control('', Validators.required),
      contactEmail: this.formBuilder.control(''),
      disableEmail: this.formBuilder.control(false),
      totalPriceSum: this.formBuilder.control(0),
      items: this.formBuilder.array([])
    });
  }

  get invoiceItems(): FormArray {
    return this.invoiceForm.get('items') as FormArray;
  }

  get automakerDocument(): string {
    return this.data.order.automaker?.company?.document || '';
  }

  get distributorDocument(): string {
    return this.data.order.distributor?.company?.document || '';
  }

  get dealershipDocument(): string {
    return this.data.order.dealership?.company?.document || '';
  }

  mapInvoiceDataSource(data: InvoiceItemDto[]) {
    return new MatTableDataSource<InvoiceItemDto>(data);
  }

  sumInvoiceTotalPrice(data: InvoiceItemDto[]) {
    return data.reduce((sum, item) => sum + (item.items.sellout || 0) * item.quantity, 0);
  }

  selectFile(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFiles = input.files;
    if (this.selectedFiles) {
      this.selectedFile = this.selectedFiles.item(0);
      this.invoiceForm.patchValue({ file: this.selectedFile.name });
    } else {
      this.selectedFile = null;
      this.invoiceForm.patchValue({ file: null });
    }
  }

  setupInvoiceForm(orderItems: OrderItemDto[]) {
    const itemsArray = this.invoiceForm.get('items') as FormArray;
    itemsArray.clear();

    this.invoiceForm.get('totalPriceSum')?.setValue(0);
    this.invoiceForm.get('disableEmail').valueChanges.subscribe((disabled) => {
      if (disabled) {
        this.invoiceForm.get('contactEmail').reset();
        this.invoiceForm.get('contactEmail').disable();
      } else {
        this.invoiceForm.get('contactEmail').enable();
      }
    });

    orderItems.forEach((item) => {
      const itemGroup = this.formBuilder.group({
        orderItemId: item.id,
        partNumber: this.formBuilder.control(item.partNumber),
        quantity: this.formBuilder.control(0, [Validators.max(item.quantity - item.quantityServed), Validators.min(0)]),
        stockDes: this.formBuilder.control(item.stockQuantity),
        unitPrice: this.formBuilder.control(item.sellout),
        totalPrice: this.formBuilder.control(0)
      });

      itemGroup.get('quantity')?.valueChanges.subscribe((quantity) => {
        const unitPrice = itemGroup.get('unitPrice')?.value || 0;
        let totalPrice = quantity * unitPrice;
        if (itemGroup.get('quantity')?.hasError('min') || itemGroup.get('quantity')?.hasError('max')) {
          totalPrice = 0;
        }
        itemGroup.get('totalPrice')?.setValue(totalPrice, { emitEvent: false });
        itemGroup.get('stockDes')?.setValue(item.stockQuantity, { emitEvent: false });
        this.updateTotalPriceSum();
      });

      itemsArray.push(itemGroup);
    });
  }

  updateTotalPriceSum() {
    const itemsArray = this.invoiceForm.get('items') as FormArray;
    const totalPriceSum = itemsArray.controls.reduce((sum, itemGroup) => {
      const totalPrice = itemGroup.get('totalPrice')?.value || 0;
      return sum + totalPrice;
    }, 0);
    this.invoiceForm.get('totalPriceSum')?.setValue(totalPriceSum);
  }

  fetchData() {
    this.loading = true;
    forkJoin({
      order: this.orderService.findById(this.orderId),
      invoices: this.orderService.findAllInvoices(this.orderId)
    })
      .subscribe({
        next: (result) => {
          this.data = result;

          const orderItems =
            this.data.order.items.map((item) => ({ ...item, quantityServed: item.quantityServed || 0, sellout: item.sellout || 0 })) ||
            [];
          let orderItemsWithPrice =
            orderItems.filter(
              (item) =>
                (this.data.order.status !== 'C' && this.data.order.status !== 'R') ||
                (item.sellout > 0 && item.quantity - item.quantityServed > 0)
            ) || [];
          orderItemsWithPrice = orderItemsWithPrice.map((item, index) => ({ ...item, index }));

          this.orderItemsDataSource = new MatTableDataSource(orderItems);
          this.orderItemsWithPriceDataSource = new MatTableDataSource(orderItemsWithPrice);

          this.setupInvoiceForm(orderItemsWithPrice);
        },
        error: () => this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao recuperar as informações' } as Alert)
      })
      .add(() => (this.loading = false));
  }

  downloadInvoiceFile(invoiceId: number) {
    this.orderService.downloadInvoiceFile(this.orderId, invoiceId);
  }

  clearForm() {
    this.selectedFile = null;
    this.invoiceForm.reset();
    this.invoiceForm.clearValidators();
    this.fetchData();
  }

  submitForm() {
    if (this.invoiceForm.invalid) {
      this.alertService.showAlert({
        title: 'Preencha o formulário corretamente para cadastrar a Nota Fiscal.',
        type: 'critical'
      } as Alert);
    } else if (this.invoiceForm.pristine) {
      this.alertService.showAlert({
        title: 'Preencha todos os campos obrigatórios (*) para cadastrar a Nota Fiscal.',
        type: 'strong'
      } as Alert);
    } else {
      const formData = this.invoiceForm.value;
      const items = formData.items
        .filter((item: any) => item.quantity > 0)
        .map((item: any) => ({
          orderItemId: item.orderItemId,
          quantity: item.quantity
        }));
      const invoice: InvoiceNoIdDto = {
        invoiceNumber: formData.invoiceNumber,
        invoiceDate: formData.invoiceDate.toISOString(),
        contactEmail: formData.contactEmail,
        items: items
      };
      this.loading = true;
      this.orderService.createInvoice(this.orderId, invoice).subscribe({
        next: (result) => {
          this.orderService
            .uploadInvoiceFile(this.orderId, result.id, this.selectedFile)
            .subscribe({
              next: () =>
                this.alertService.showAlert({
                  type: 'success',
                  title: 'Lançamento efetuado com sucesso!'
                } as Alert),
              error: () =>
                this.alertService.showAlert({
                  type: 'critical',
                  title: 'Ocorreu um erro ao enviar o arquivo da Nota Fiscal.'
                } as Alert)
            })
            .add(() => {
              this.loading = false;
              this.clearForm();
            });
        },
        error: () => {
          this.alertService.showAlert({
            type: 'critical',
            title: 'Ocorreu um erro ao realizar o lançamento.'
          } as Alert);
          this.loading = false;
        }
      });
    }
  }

  ngOnInit(): void {
    this.orderId = this.activatedRoute.snapshot.params['id'];
    this.fetchData();
  }

  cancelItem(item: OrderDto) {
    this.selectedItem = item;
    this.modalConfirmCancelItem.open({
      id: 'modalConfirmCancel',
      width: 768,
      title: 'Cancelamento de Pedido',
      content: this.contentModalConfirmCancelItem,
      cancel: { text: 'Cancelar', icon: 'close' },
      confirm: { text: 'Confirmar', icon: 'done', color: 'warn' }
    });
    this.loading = false;
  }

  onModalConfirmCancelItemClosed(result: ModalResult): void {
    if (result.result) {
      this.loading = true;
      this.orderService
        .cancelItem(this.selectedItem?.id)
        .subscribe({
          next: () => {
            this.showAlert(AlertType.success, 'Item cancelado com sucesso!');
            this.clearForm();
          },
          error: () =>
            this.alertService.showAlert({
              type: AlertType.strong,
              content: 'Erro ao cancelar o item.',
              timestamp: 5000
            } as Alert)
        })
        .add(() => {
          this.loading = false;
          this.clearForm();
        });
    }
  }

  showAlert(type: AlertType, content: string) {
    this.alertService.showAlert({
      type: type,
      content: content
    } as Alert);
  }

  isDisabled(item: any): boolean {
    this.userData = this.authService.getUserData();
    let authorize = false;

    switch (this.userData.userProfile.userType) {
      case 'AUTOMAKER':
        if (this.data.order.automaker.orderCancellationAutomaker) {
          authorize = true;
        }
        break;
      case 'DISTRIBUTOR':
        if (this.data.order.automaker.orderCancellationDistributor) {
          authorize = true;
        }
        break;
      case 'MICHELIN':
        if (this.data.order.automaker.orderCancellationMichelin) {
          authorize = true;
        }
        break;
      default:
        authorize = false;
        break;
    }
    if (item.percent === undefined) {
      item.percent = '0%';
    }
    return (
      !authorize ||
      item.percent !== '0%' ||
      item.status === 'C' ||
      item.status === 'R' ||
      item.status === 'F' ||
      this.data.order.status === 'C'
    );
  }

  isCanceled(item: any): boolean {
    return item.status === 'C' || this.data.order.status === 'C';
  }

  statusMap: Record<string, string> = {
    O: 'Em Aberto',
    P: 'Em Andamento',
    F: 'Finalizado',
    R: 'Recusado',
    C: 'Cancelado'
  };
}
